import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Radio, Clock, ChevronLeft, ChevronRight, Tv, Loader2 } from "lucide-react";
import { ReminderBell } from "../components/ReminderBell";

const HOURS = 6;
const PIXELS_PER_MINUTE = 12;  // 30 min slot = 360px

const fmtTime = (iso) => {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

/** Electronic Program Guide — TV-style timetable across all channels. */
export default function ProgramGuide() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());
  const scrollRef = useRef(null);

  useEffect(() => {
    api
      .get(`/epg/schedule?hours=${HOURS}`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  // Ticking clock (updates the red NOW line every 30s)
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  // Scroll auto to the NOW line on first load
  useEffect(() => {
    if (!data || !scrollRef.current) return;
    const gridStart = new Date(data.grid_start);
    const minutesFromStart = (now - gridStart) / 60000;
    const target = Math.max(0, minutesFromStart * PIXELS_PER_MINUTE - 200);
    scrollRef.current.scrollLeft = target;
  }, [data, now]);

  const slots = useMemo(() => {
    if (!data) return [];
    const start = new Date(data.grid_start);
    const out = [];
    for (let i = 0; i <= HOURS * 2; i++) {
      const t = new Date(start.getTime() + i * 30 * 60000);
      out.push(t);
    }
    return out;
  }, [data]);

  const gridWidthPx = HOURS * 60 * PIXELS_PER_MINUTE;

  const minutesFromStart = (iso) => {
    if (!data) return 0;
    return (new Date(iso) - new Date(data.grid_start)) / 60000;
  };

  const nowPx = data ? Math.max(0, (now - new Date(data.grid_start)) / 60000 * PIXELS_PER_MINUTE) : 0;
  const inGrid = data && now >= new Date(data.grid_start) && now <= new Date(data.grid_end);

  const scrollByPx = (delta) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: delta, behavior: "smooth" });
    }
  };

  return (
    <div className="max-w-[1600px] mx-auto px-6 py-8" data-testid="epg-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--accent)] mb-3 flex items-center gap-2">
          <Tv className="w-3.5 h-3.5" /> Grille des programmes · {HOURS}h glissantes
        </div>
        <div className="flex items-end justify-between flex-wrap gap-4">
          <h1 className="font-display text-5xl sm:text-6xl leading-none">Programme TV</h1>
          <div className="flex items-center gap-2">
            <Link
              to="/live"
              data-testid="epg-live-cta"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/40 text-red-400 hover:bg-red-500/20 text-xs uppercase tracking-widest font-semibold transition"
            >
              <span className="relative inline-flex w-1.5 h-1.5">
                <span className="absolute inset-0 rounded-full bg-red-500 animate-ping" />
                <span className="relative rounded-full bg-red-500 w-1.5 h-1.5" />
              </span>
              Direct
            </Link>
            <button
              onClick={() => scrollByPx(-PIXELS_PER_MINUTE * 60)}
              data-testid="epg-scroll-prev"
              title="Reculer 1h"
              className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[color:var(--border)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollByPx(PIXELS_PER_MINUTE * 60)}
              data-testid="epg-scroll-next"
              title="Avancer 1h"
              className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[color:var(--border)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
        <p className="text-[color:var(--text-muted)] mt-3 max-w-2xl">
          {"Ce qui passe maintenant, ce qui arrive ensuite. Toute la programmation Fedevo 2.0 d'un coup d'œil."}
        </p>
      </div>

      {loading && (
        <div className="py-20 text-center text-[color:var(--text-muted)]">
          <Loader2 className="w-8 h-8 mx-auto animate-spin mb-3" />
          Chargement de la grille...
        </div>
      )}

      {!loading && data && (
        <div
          className="rounded-2xl border border-[color:var(--border)] bg-[color:var(--bg-elev)] overflow-hidden"
          data-testid="epg-grid-wrap"
        >
          <div className="flex">
            {/* Sticky channel column */}
            <div className="shrink-0 w-44 sm:w-52 bg-[color:var(--bg-elev)] border-r border-[color:var(--border)]">
              <div className="h-14 border-b border-[color:var(--border)] flex items-center px-4 text-[10px] uppercase tracking-[0.3em] text-[color:var(--text-muted)]">
                Chaînes
              </div>
              {data.channels.map(ch => (
                <Link
                  to={ch.shorts_only ? `/shorts/${ch.id}` : `/channel/${ch.id}`}
                  key={ch.id}
                  data-testid={`epg-channel-${ch.slug}`}
                  className="h-24 px-4 flex items-center gap-3 border-b border-[color:var(--border)] hover:bg-[color:var(--bg-elev-2)] transition group"
                >
                  {ch.logo ? (
                    <img src={ch.logo} alt={ch.name} className="w-10 h-10 object-contain rounded bg-black/40 p-1 shrink-0" />
                  ) : (
                    <span className="w-10 h-10 rounded shrink-0 font-display text-xl flex items-center justify-center text-white" style={{ background: ch.color }}>
                      {ch.name?.[0]}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate group-hover:text-[color:var(--accent)] transition">{ch.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] truncate">{ch.category}</div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Scrollable timeline */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-x-auto overflow-y-hidden relative scrollbar-thin"
              data-testid="epg-timeline-scroll"
            >
              {/* Time header */}
              <div className="h-14 border-b border-[color:var(--border)] flex items-end pb-2 sticky top-0 bg-[color:var(--bg-elev)] z-10 relative" style={{ width: gridWidthPx, minWidth: gridWidthPx }}>
                {slots.map((slot) => (
                  <div
                    key={slot.toISOString()}
                    className="border-l border-[color:var(--border)] px-2 shrink-0 text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-mono"
                    style={{ width: 30 * PIXELS_PER_MINUTE, minWidth: 30 * PIXELS_PER_MINUTE }}
                  >
                    {slot.getMinutes() === 0 ? (
                      <span className="text-white text-xs">{fmtTime(slot.toISOString())}</span>
                    ) : (
                      <span className="opacity-50">:{String(slot.getMinutes()).padStart(2, "0")}</span>
                    )}
                  </div>
                ))}
                {/* NOW marker in header */}
                {inGrid && (
                  <div
                    className="absolute top-0 bottom-0 z-20 pointer-events-none"
                    style={{ left: nowPx }}
                    data-testid="epg-now-marker"
                  >
                    <div className="absolute -top-1 -left-2 px-2 py-0.5 bg-red-500 text-white text-[9px] uppercase tracking-widest rounded font-bold whitespace-nowrap">
                      NOW · {String(now.getHours()).padStart(2, "0")}:{String(now.getMinutes()).padStart(2, "0")}
                    </div>
                  </div>
                )}
              </div>

              {/* Rows of programs */}
              <div className="relative" style={{ width: gridWidthPx, minWidth: gridWidthPx }}>
                {data.channels.map((ch) => (
                  <div
                    key={ch.id}
                    className="relative h-24 border-b border-[color:var(--border)]"
                    data-testid={`epg-row-${ch.slug}`}
                  >
                    {ch.programs.map((p, idx) => {
                      const offsetMin = minutesFromStart(p.start);
                      const widthMin = p.duration_sec / 60;
                      const isLive = inGrid && new Date(p.start) <= now && new Date(p.end) > now;
                      return (
                        <Link
                          to={`/watch/${p.video_id}`}
                          key={`${p.video_id}-${idx}`}
                          data-testid={`epg-program-${ch.slug}-${idx}`}
                          title={`${p.title}\n${fmtTime(p.start)} → ${fmtTime(p.end)}`}
                          className={`absolute top-1.5 bottom-1.5 rounded-lg overflow-hidden border transition-all group cursor-pointer ${
                            isLive
                              ? "border-[color:var(--accent)] ring-2 ring-[color:var(--accent)]/60 z-10"
                              : "border-[color:var(--border)] hover:border-[color:var(--accent)]/60 hover:z-10"
                          }`}
                          style={{
                            left: offsetMin * PIXELS_PER_MINUTE + 2,
                            width: Math.max(50, widthMin * PIXELS_PER_MINUTE - 4),
                            background: `linear-gradient(180deg, ${ch.color}30, var(--bg))`,
                          }}
                        >
                          <div className="flex h-full relative">
                            {p.thumbnail && widthMin > 4 && (
                              <img
                                src={p.thumbnail}
                                alt=""
                                className="h-full aspect-video object-cover shrink-0"
                                loading="lazy"
                              />
                            )}
                            <div className="flex-1 min-w-0 px-2 py-1.5 flex flex-col justify-between">
                              <div className="text-[10px] font-mono text-[color:var(--accent)] flex items-center gap-1">
                                {isLive && (
                                  <span className="inline-flex items-center gap-1 text-red-400">
                                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                                    LIVE
                                  </span>
                                )}
                                <span>{fmtTime(p.start)}</span>
                              </div>
                              <div className={`text-[11px] leading-tight ${widthMin > 6 ? "line-clamp-2" : "line-clamp-1"} text-white group-hover:text-[color:var(--accent)] transition`}>
                                {p.title}
                              </div>
                            </div>
                            {!isLive && new Date(p.start) > now && (
                              <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <ReminderBell program={p} channelName={ch.name} compact />
                              </div>
                            )}
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                ))}

                {/* Continuous NOW vertical line across all rows */}
                {inGrid && (
                  <div
                    className="absolute top-0 bottom-0 w-px bg-red-500 z-20 pointer-events-none shadow-[0_0_8px_rgba(239,68,68,0.8)]"
                    style={{ left: nowPx }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] flex items-center gap-3 flex-wrap">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2 h-2 rounded bg-red-500" /> Position actuelle
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Clock className="w-3 h-3" /> Programme stable sur 24h, mélangé chaque jour
        </span>
        <span className="inline-flex items-center gap-1.5 ml-auto">
          <Radio className="w-3 h-3 text-[color:var(--accent)]" /> Cliquez sur un créneau pour regarder
        </span>
      </div>
    </div>
  );
}
